import { Box, Stack, Typography } from '@mui/material';
import { ContractPageVersion, GetContractPageDataResponse } from '@vy/api-definition';
import React, { useMemo } from 'react';
import { useGetPhaseLabels } from '../../../../../hooks/requests/use-phase-labels';
import { buildPeriod, findRequired, isNullOrUndefined, validateNotNullOrUndefined, vyDate } from '@vy/utils';
import { Period, PhaseLabel } from '@vy/types';
import { getLabelIcon } from '../contract-content-utils/get-phase-label-icon';
import {
  ContractPhasesTimelineContainer,
  PeriodsData,
} from '../ContractPhasesSection/ContractPhasesTimeline/ContractPhasesTimelineContainer';
import { ContractPhasesRowBar } from '../ContractPhasesSection/ContractPhasesTimeline/ContractPhasesRowBar';
import { formatDate } from '../../../../../utils/date';

export type ContractPhaseTimelineSectionProps = {
  contractPageData: GetContractPageDataResponse['contractPageData'];
};

export const ContractPhaseTimelineSection: React.FC<ContractPhaseTimelineSectionProps> = ({
  contractPageData,
}) => {
  const phaseLabelsData = useGetPhaseLabels({ suspense: true });
  validateNotNullOrUndefined(phaseLabelsData);
  const { phaseLabels } = phaseLabelsData;

  const { contract } = contractPageData;

  const contractLength = useMemo(() => buildContractPeriod(contract.startDate, contract.endDate), [contract]);

  const timelineChartData = useMemo(() => {
    const { versions } = contractPageData;

    return buildVersionsTimelineChartData(contractLength, versions, phaseLabels);
  }, [contractPageData, phaseLabels]);

  const versionAmount = contractPageData.versions.length;

  const contractDurationInMonths = getContractDurationInMonths(
    contractLength.startTime,
    contractLength.endTime,
  );

  return (
    <Stack direction="column" sx={{
      width: "100%"
    }}>
      <Typography variant="body1" sx={{
        padding: "16px 0"
      }}>
        Contract effective from <b>{formatStartDate(contractLength.startTime)}</b> - this version includes{' '}
        <b>{versionAmount} phases</b> over <b>{contractDurationInMonths}</b>.
      </Typography>
      <Box>
        <ContractPhasesTimelineContainer
          contractLength={contractLength}
          chartData={timelineChartData}
          BarComponent={ContractPhasesRowBar}
        />
      </Box>
    </Stack>
  );
};

function buildVersionsTimelineChartData(
  contractPeriod: Period,
  versions: ContractPageVersion[],
  phaseLabels: PhaseLabel[],
) {
  return versions.map<PeriodsData>((version, index) => {
    const label = findRequired(phaseLabels, (phaseLabel) => phaseLabel.type === version.label);
    const icon = getLabelIcon(version.label);
    const phaseName = version.name ?? `Phase ${index + 1}`;
    const title = phaseName === label.textLabel ? label.textLabel : `${phaseName} - ${label.textLabel}`;

    const nextVersion = versions.at(index + 1);

    const isLastVersion = isNullOrUndefined(nextVersion);
    const isOverlappingVersion = vyDate(version.effectiveAt).isSameOrAfter(nextVersion?.effectiveAt, 'day');

    const endTime = isLastVersion || isOverlappingVersion ? contractPeriod.endTime : nextVersion.effectiveAt;

    const period = buildPeriod(version.effectiveAt, endTime);

    return {
      period,
      title,
      backgroundColor: label.backgroundColor,
      color: label.color,
      icon,
    };
  });
}

function formatStartDate(startDate: Date): string {
  return formatDate(startDate, 'MMM DD, YYYY');
}

function buildContractPeriod(startDate: Date, endDate?: Date): Period {
  const defaultEndDate = vyDate(startDate).add(1, 'year').toDate();

  return {
    startTime: startDate,
    endTime: endDate ?? defaultEndDate,
  };
}

function getContractDurationInMonths(startDate: Date, endDate: Date) {
  const vyDateStart = vyDate(startDate);
  const vyDateEnd = vyDate(endDate);

  const monthsAmount = vyDateEnd.diff(vyDateStart, 'months', true);

  const moreThanOneMonth = monthsAmount > 1;

  return `${Math.ceil(monthsAmount)} month${moreThanOneMonth ? 's' : ''}`;
}
